import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  Server,
  FileText,
  ExternalLink,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
} from "lucide-react";

const GRAFANA_BASE = process.env.REACT_APP_GRAFANA_URL || "/grafana";

const DASHBOARDS = [
  {
    id: "backend",
    icon: Activity,
    uid: "mawered-backend",
    slug: "mawered-backend-api",
    color: "from-blue-500/20 to-blue-600/10",
    iconColor: "text-blue-400",
  },
  {
    id: "logs",
    icon: FileText,
    uid: "mawered-logs",
    slug: "mawered-logs-applicatifs",
    color: "from-orange-500/20 to-orange-600/10",
    iconColor: "text-orange-400",
  },
  {
    id: "system",
    icon: Server,
    uid: "mawered-system",
    slug: "mawered-systeme",
    color: "from-green-500/20 to-green-600/10",
    iconColor: "text-green-400",
  },
];

const GrafanaStatus = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    const check = async () => {
      try {
        const resp = await fetch(`${GRAFANA_BASE}/api/health`, {
          signal: AbortSignal.timeout(4000),
        });
        setStatus(resp.ok ? "up" : "down");
      } catch {
        setStatus("down");
      }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []);

  if (status === "checking") {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <RefreshCw className="h-3 w-3 animate-spin" />
        {t("monitoringHub.status.checking")}
      </span>
    );
  }
  if (status === "up") {
    return (
      <span className="flex items-center gap-1.5 text-xs text-emerald-400">
        <CheckCircle className="h-3 w-3" />
        {t("monitoringHub.status.up")}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-xs text-red-400">
      <XCircle className="h-3 w-3" />
      {t("monitoringHub.status.down")}
    </span>
  );
};

const DashboardCard = ({ dashboard, isActive, onClick }) => {
  const { t } = useTranslation();
  const Icon = dashboard.icon;
  return (
    <button
      onClick={() => onClick(dashboard.id)}
      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
        isActive
          ? "border-primary bg-primary/10 shadow-sm"
          : "border-border bg-card hover:border-primary/40 hover:bg-card/80"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-gradient-to-br ${dashboard.color} flex-shrink-0`}>
          <Icon className={`h-4 w-4 ${dashboard.iconColor}`} />
        </div>
        <div className="min-w-0">
          <p className={`text-sm font-medium truncate ${isActive ? "text-primary" : "text-foreground"}`}>
            {t(`monitoringHub.dashboards.${dashboard.id}.label`)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {t(`monitoringHub.dashboards.${dashboard.id}.description`)}
          </p>
        </div>
      </div>
    </button>
  );
};

const IframePanel = ({ dashboard }) => {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const url = `${GRAFANA_BASE}/d/${dashboard.uid}/${dashboard.slug}?orgId=1&refresh=30s&kiosk=tv`;

  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [dashboard.uid]);

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden border border-border bg-card">
      {!loaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t("monitoringHub.iframe.loading")}</p>
          </div>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
          <div className="flex flex-col items-center gap-3 text-center px-8">
            <AlertCircle className="h-8 w-8 text-amber-400" />
            <p className="text-sm font-medium">{t("monitoringHub.iframe.notAccessible")}</p>
            <p className="text-xs text-muted-foreground">
              {t("monitoringHub.iframe.notAccessibleHint")}
            </p>
            <a
              href={`${GRAFANA_BASE}/d/${dashboard.uid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-primary hover:underline mt-2"
            >
              <ExternalLink className="h-3 w-3" />
              {t("monitoringHub.iframe.openDirect")}
            </a>
          </div>
        </div>
      )}
      <iframe
        key={dashboard.uid}
        src={url}
        title={t(`monitoringHub.dashboards.${dashboard.id}.label`)}
        className="w-full h-full border-0"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
};

export default function MonitoringHub() {
  const { t } = useTranslation();
  const [active, setActive] = useState("backend");

  const activeDashboard = DASHBOARDS.find((d) => d.id === active);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] gap-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <BarChart3 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">{t("monitoringHub.title")}</h1>
            <p className="text-xs text-muted-foreground">{t("monitoringHub.subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <GrafanaStatus />
          <a
            href={`${GRAFANA_BASE}/dashboards`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors border border-border rounded-lg px-3 py-1.5 hover:border-primary/40"
          >
            <ExternalLink className="h-3 w-3" />
            {t("monitoringHub.openGrafana")}
          </a>
        </div>
      </div>

      {/* Content */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Sidebar */}
        <div className="flex flex-col gap-2 w-64 flex-shrink-0">
          <p className="text-xs text-muted-foreground font-medium px-1 uppercase tracking-wider">
            {t("monitoringHub.sidebar.dashboards")}
          </p>
          {DASHBOARDS.map((d) => (
            <DashboardCard
              key={d.id}
              dashboard={d}
              isActive={active === d.id}
              onClick={setActive}
            />
          ))}

          {/* Quick links */}
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground font-medium px-1 uppercase tracking-wider mb-2">
              {t("monitoringHub.sidebar.quickAccess")}
            </p>
            {[
              { key: "alerts", path: "/alerting/list" },
              { key: "logs", path: "/explore?orgId=1&left=%7B%22datasource%22%3A%22loki-mawered%22%7D" },
              { key: "metrics", path: "/explore?orgId=1&left=%7B%22datasource%22%3A%22prometheus-mawered%22%7D" },
            ].map((link) => (
              <a
                key={link.path}
                href={`${GRAFANA_BASE}${link.path}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground py-1.5 px-1 rounded hover:bg-muted/50 transition-colors"
              >
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
                {t(`monitoringHub.quickLinks.${link.key}`)}
              </a>
            ))}
          </div>
        </div>

        {/* Iframe zone */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="flex items-center justify-between flex-shrink-0">
            <p className="text-sm font-medium">
              {activeDashboard && t(`monitoringHub.dashboards.${activeDashboard.id}.label`)}
            </p>
            <a
              href={`${GRAFANA_BASE}/d/${activeDashboard?.uid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              {t("monitoringHub.fullscreen")}
            </a>
          </div>
          <div className="flex-1 min-h-0">
            {activeDashboard && <IframePanel dashboard={activeDashboard} />}
          </div>
        </div>
      </div>
    </div>
  );
}
